/**
 * finalizeMultiEditedCell.ts - Pure mutation function for committing a multi-window cell edit.
 *
 * When a cell is edited in the multi-window editor, the committed value can come from either the
 * typed formula text or a structured `typedValue` (e.g. a chip/storable). This function encodes the
 * precedence between those two sources so it can be unit tested independently of the
 * `TableEditable` multi-window editing UI.
 */

import produce from 'immer';
import isEqual from 'react-fast-compare';

import {type Table} from 'notebook/tables/constants/TableConstants';
import {type TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {type TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

/**
 * Compute the table that results from finalizing a multi-window cell edit.
 *
 * Precedence rules:
 *   - A non-empty `typedValue` wins: it is stored and the cell text is cleared.
 *   - Otherwise the `typedValue` is cleared (set to null) and the formula text is stored.
 *
 * @param table - The current table
 * @param index - The [row, column] index of the edited cell
 * @param formula - The text typed into the cell editor
 * @param typedValue - The structured value for the cell, or null if there is none
 * @returns The updated table, or `null` if the cell is unchanged (no commit needed)
 */
export function finalizeMultiEditedCell(
  table: Table,
  index: TableIndex,
  formula: string,
  typedValue: TypedValue | null
): Table | null {
  const [i, j] = index;
  const oldCell = table.table[i][j];
  const oldTypedValue = oldCell.typedValue ?? null;

  // Skip updating the table if the cell hasn't changed.
  if (formula === oldCell.text && isEqual(typedValue, oldTypedValue)) {
    return null;
  }

  const newCell = TableUtil.makeCell(formula);
  if (typedValue != null && !isTypedValueEmpty(typedValue)) {
    newCell.typedValue = typedValue;
    newCell.text = '';
  } else {
    newCell.typedValue = null;
  }

  return produce(table, (draftTable) => {
    draftTable.table[i][j] = {...draftTable.table[i][j], ...newCell};
  });
}
